<script setup lang="ts">
    import { formatAmount, formatDate } from '@/utils';
    import { useRoute } from 'vue-router';
    import { ref, computed, onMounted, watch } from 'vue';
    import axios from 'axios';
    import moment from 'moment';

    const route = useRoute();
    const user: any = ref(route.meta.user || {});

    interface Carte {
        id: number;
        alias: string | null;
        numero_masque: string | null;
        bloc4: string | null;
        type: string | null;
        statut: string;
        solde: number;
        expiration_mois: number | null;
        expiration_annee: number | null;
        reference: string | null;
        num_lot: string | null;
        date_retrait: string | null;
        created_at: string | null;
        par_defaut: boolean;
        banque: { id: number; nom: string; slug: string } | null;
    }

    interface Transaction {
        id: number;
        reference: string | null;
        libelle: string | null;
        description: string | null;
        montant: number;
        devise: string | null;
        type: string;
        statut: string;
        categorie_slug: string | null;
        created_at: string | null;
    }

    const cartes = ref<Carte[]>([]);
    const cartesLoading = ref(false);
    const selectedCarteId = ref<number | null>(null);

    const transactions = ref<Transaction[]>([]);
    const transactionsTotal = ref(0);
    const transactionsLoading = ref(false);
    const txPage = ref(1);
    const txPerPage = ref(25);
    const txReference = ref('');
    const txDateMin = ref('');
    const txDateMax = ref('');

    const blocking = ref(false);
    const retraitForm = ref({
        telephone: '',
        nom: '',
        prenom: '',
        numero_piece: '',
        bloc4: '',
        code_retrait: '',
    });
    const retraitLoading = ref(false);

    const selectedCarte = computed<Carte | null>(() => {
        return cartes.value.find(c => c.id === selectedCarteId.value) || cartes.value[0] || null;
    });

    watch(selectedCarte, (carte) => {
        if (carte?.bloc4) {
            retraitForm.value.bloc4 = `**** **** **** ${carte.bloc4}`;
        } else {
            retraitForm.value.bloc4 = '';
        }
    });

    const totalPages = computed(() => Math.ceil(transactionsTotal.value / txPerPage.value) || 1);

    onMounted(async () => {
        // Recharger les données utilisateur
        if (route.params.id) {
            try {
                const response = await axios.get(`/backoffice/users/get/${route.params.id}`);
                user.value = response.data;
                retraitForm.value.nom = user.value?.nom || '';
                retraitForm.value.prenom = user.value?.prenom || '';
                const kycDocs = user.value?.kyc?.kyc_documents || [];
                const idDoc = kycDocs.find((d: any) => d?.type_document === 'cni_recto' || d?.type_document === 'passeport');
                retraitForm.value.numero_piece = idDoc?.numero || user.value?.numero_piece_identite || '';
            } catch (error: any) {
                console.error('Erreur chargement user:', error);
            }
            await loadCartes();
        }
    });

    async function loadCartes() {
        if (!route.params.id) return;
        cartesLoading.value = true;
        try {
            const response = await axios.get(`/backoffice/users/${route.params.id}/cartes`);
            cartes.value = response.data.cartes || [];
            if (cartes.value.length > 0 && selectedCarteId.value === null) {
                selectedCarteId.value = cartes.value[0].id;
                await loadTransactions();
            }
        } catch (error: any) {
            console.error('Erreur chargement cartes:', error);
        } finally {
            cartesLoading.value = false;
        }
    }

    async function loadTransactions() {
        if (!route.params.id || !selectedCarte.value) return;
        transactionsLoading.value = true;
        try {
            const response = await axios.get(
                `/backoffice/users/${route.params.id}/cartes/${selectedCarte.value.id}/transactions`,
                {
                    params: {
                        reference: txReference.value || undefined,
                        date_min: txDateMin.value || undefined,
                        date_max: txDateMax.value || undefined,
                        page: txPage.value,
                        per_page: txPerPage.value,
                    },
                },
            );
            transactions.value = response.data.data || [];
            transactionsTotal.value = response.data.total || 0;
        } catch (error: any) {
            console.error('Erreur chargement transactions:', error);
        } finally {
            transactionsLoading.value = false;
        }
    }

    async function bloquerCarte() {
        if (!selectedCarte.value || blocking.value) return;
        if (!confirm('Voulez-vous vraiment bloquer cette carte ?')) return;
        blocking.value = true;
        try {
            await axios.post(`/backoffice/users/${route.params.id}/cartes/${selectedCarte.value.id}/bloquer`);
            await loadCartes();
        } catch (error: any) {
            alert(error.response?.data?.message || 'Erreur lors du blocage.');
        } finally {
            blocking.value = false;
        }
    }

    async function retraitCarte() {
        if (!selectedCarte.value || retraitLoading.value) return;
        if (!retraitForm.value.bloc4 || !retraitForm.value.code_retrait || !retraitForm.value.telephone) {
            alert('Le téléphone, les 4 derniers chiffres et le code de retrait sont obligatoires.');
            return;
        }
        retraitLoading.value = true;
        try {
            const bloc4Clean = retraitForm.value.bloc4.replace(/\D/g, '').slice(-4);
            await axios.post(
                `/backoffice/users/${route.params.id}/cartes/${selectedCarte.value.id}/retrait`,
                {
                    bloc4: bloc4Clean,
                    code_retrait: retraitForm.value.code_retrait,
                    telephone: retraitForm.value.telephone,
                },
            );
            alert('Carte retirée avec succès.');
            retraitForm.value = { telephone: '', nom: '', prenom: '', numero_piece: '', bloc4: '', code_retrait: '' };
            await loadCartes();
        } catch (error: any) {
            alert(error.response?.data?.message || 'Erreur lors du retrait.');
        } finally {
            retraitLoading.value = false;
        }
    }

    function applyTxFilters() {
        txPage.value = 1;
        loadTransactions();
    }

    function changePage(page: number) {
        if (page < 1 || page > totalPages.value) return;
        txPage.value = page;
        loadTransactions();
    }

    function formatDateFrenchLong(dateString?: string | null): string {
        if (!dateString) return '-';
        const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        const date = moment(dateString);
        if (!date.isValid()) return '-';
        return `${date.format('DD')} ${monthsFr[date.month()]} ${date.year()}`;
    }
</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <h4 class="s7 font-semibold px-3 pb-5">Membre / Carte <span class="text-sm text-grey-500"></span></h4>

        <!--User info - Component-->
        <div class="s20 widget flex align-items-center px-1 py-5">
            <div class="s2_mobile h-100p px-7">
                <div class="rounded-100 overflow-hidden item active">
                    <img :src="!!user.photo_url ? user.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                </div>
            </div>
            <div class="s4_mobile h-100p px-2">
                <h3 class="mb-2">{{ user.email || user.telephone || 'N/A' }}</h3>
                <h4>{{ user.nom + ' ' + user.prenom }}</h4>
            </div>
            <div class="s3_mobile h-100p px-2">
                <h4 class="font-normal mb-5">{{ user.collectivite?.nom || 'N/A' }}</h4>
                <span v-if="user.actif == 1" class="tag-state notif stroke">Actif</span>
                <span v-else class="tag-state danger stroke">Inactif</span>
            </div>
            <div class="s4_mobile h-100p px-3">
                <h4 class="font-normal" v-if="user.souscription?.date_fin">
                    Date de fin de souscription <br class="mb-2"/>
                    <b class="text-2xl">{{ formatDateFrenchLong(user.souscription.date_fin) }}</b>
                </h4>
                <h4 class="font-normal" v-else>
                    Date de fin de souscription <br class="mb-2"/>
                    <b class="text-2xl">-</b>
                </h4>
            </div>
            <div class="s7_mobile h-100p px-5">
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                    <div class="widget-floating py-0 text-xl flex-right">Solde</div>
                    <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" style="width: 100%;">
                        {{ selectedCarte ? formatAmount(selectedCarte.solde) + ' F' : '-' }}
                    </div>
                </div>
            </div>
        </div>
        <!--End User info-->

        <div class="clear-both h-20"></div>

        <!--Carte - Component-->
        <div v-if="cartesLoading" class="text-center py-5 text-grey-500">Chargement des cartes...</div>

        <div v-else-if="cartes.length === 0" class="text-center py-5 text-grey-500">
            Aucune carte associée à ce membre.
        </div>

        <div v-else-if="selectedCarte" class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Carte
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right gap-3">
                <button
                    v-if="selectedCarte.statut !== 'blocked'"
                    class="btn-ultra lg shadow-200 color2"
                    :disabled="blocking"
                    @click="bloquerCarte"
                >
                    <div class="btn-show" :class="{ 'display-none': blocking }">
                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                        <span>Bloquer</span>
                    </div>
                    <div class="btn-loading" :class="{ 'display-none': !blocking }">
                        <div class="loader-dots"></div>
                    </div>
                </button>
                <span v-else class="tag-state danger stroke">Bloquée</span>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 gap-6 px-5 pb-6 collapse-content active">
                <!-- Formulaire Retrait de carte (visible uniquement si statut = delivery) -->
                <div v-if="selectedCarte.statut === 'delivery'" class="s20 position-relative overflow-hidden rounded-100 mt-6 mb-10" style="background-color: rgba(var(--color-black-rgb), 0.05);">
                    <div class="s20 position-relative rounded-100 py-7 px-8">
                        <h3 class="mb-2 px-5">Retrait de carte</h3>
                        <div class="s10 px-4 my-3">
                            <div class="form-floating w-100 my-5">
                                <input v-model="retraitForm.telephone" type="text" class="form-control-code my-0 px-8 py-5 bg-white rounded-100 text-left" placeholder="Numéro de téléphone du membre">
                            </div>
                            <div class="form-floating w-100 my-5">
                                <input v-model="retraitForm.nom" type="text" class="form-control-code my-0 px-8 py-5 bg-white rounded-100 text-left" placeholder="Nom">
                            </div>
                            <div class="form-floating w-100 my-5">
                                <input v-model="retraitForm.prenom" type="text" class="form-control-code my-0 px-8 py-5 bg-white rounded-100 text-left" placeholder="Prénom">
                            </div>
                        </div>
                        <div class="s10 px-4 my-3">
                            <div class="form-floating w-100 my-5">
                                <input v-model="retraitForm.numero_piece" type="text" class="form-control-code my-0 px-8 py-5 bg-white rounded-100 text-left" placeholder="Numéro de la pièce d'identité">
                            </div>
                            <div class="form-floating w-100 my-5">
                                <input v-model="retraitForm.bloc4" type="text" class="form-control-code my-0 px-8 py-5 bg-white rounded-100 text-left" placeholder="Les 4 derniers chiffres de la carte" maxlength="4">
                            </div>
                            <div class="form-floating w-100 my-5">
                                <input v-model="retraitForm.code_retrait" type="text" class="form-control-code my-0 px-8 py-5 bg-white rounded-100 text-left" placeholder="Code de retrait de la carte">
                            </div>
                            <button
                                class="btn-ultra lg w-100 shadow-0"
                                :disabled="retraitLoading"
                                @click="retraitCarte"
                            >
                                <div class="btn-show" :class="{ 'display-none': retraitLoading }">
                                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                    <span>Valider</span>
                                </div>
                                <div class="btn-loading" :class="{ 'display-none': !retraitLoading }">
                                    <div class="loader-dots"></div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="s20 flex gap-5">
                    <div class="s6_mobile">
                        <img src="/images/carte_coop.png" alt="Carte" width="100%" class="rounded-30" />
                    </div>
                    <div class="flex-center">
                        <hr class="hr-vertical h-100p"/>
                    </div>
                    <div class="s7_mobile px-6">
                        <h3 class="font-normal text-primary-color mb-4">
                            Carte &nbsp;
                            <span v-if="selectedCarte.statut === 'active'" class="tag-state notif stroke">Validé</span>
                            <span v-else-if="selectedCarte.statut === 'blocked'" class="tag-state danger stroke">Bloquée</span>
                            <span v-else class="tag-state warning stroke">{{ selectedCarte.statut }}</span>
                        </h3>
                        <h4 class="text-grey-900 my-2">**** **** **** {{ selectedCarte.bloc4 || '****' }}</h4>
                        <h4 class="text-grey-900 my-2">{{ selectedCarte.banque?.nom || 'N/A' }}</h4>
                        <h5 v-if="selectedCarte.created_at" class="font-normal my-2 text-grey-800">
                            Créé le {{ formatDate(selectedCarte.created_at, 'DD MMM YYYY HH:mm') }}
                        </h5>
                        <div class="clear-both h-10"></div>
                        <h4 class="text-grey-800">Référence / {{ selectedCarte.reference || '-' }}</h4>
                        <div class="clear-both h-10"></div>
                        <h4 class="font-normal mb-2 text-grey-800">Solde disponible</h4>
                        <h4 class="mb-2 text-grey-800">{{ formatAmount(selectedCarte.solde) }} F</h4>
                    </div>
                    <div class="flex-center">
                        <hr class="hr-vertical h-100p"/>
                    </div>

                    <div class="s7_mobile px-6">
                        <h3 class="font-normal text-primary-color mb-4">Retrait &nbsp;
                            <span v-if="selectedCarte.date_retrait" class="tag-state notif stroke">Effectué</span>
                            <span v-else class="tag-state warning stroke">En attente</span>
                        </h3>
                        <h5 v-if="selectedCarte.date_retrait" class="font-normal my-2 text-grey-800">
                            Retiré le {{ formatDate(selectedCarte.date_retrait, 'DD MMM YYYY HH:mm') }}
                        </h5>
                        <h5 v-else class="font-normal my-2 text-grey-500">Carte non encore retirée</h5>
                        <div class="clear-both h-10"></div>
                        <h4 class="text-grey-800" v-if="selectedCarte.num_lot">N° de lot : {{ selectedCarte.num_lot }}</h4>
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Transactions-->
        <div v-if="selectedCarte" class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Transactions
                </span>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 gap-6 px-4 pb-6 collapse-content active">
                <!-- Filtres -->
                <div class="s20 flex gap-3 mb-2">
                    <div class="form-field-split my-4">
                        <div class="form-field-floating reactive search sm">
                            <input v-model="txReference" type="text" class="form-input" name="reference" id="reference" placeholder="" @keyup.enter="applyTxFilters">
                            <label for="reference">Référence de transaction</label>
                        </div>
                    </div>
                    <div class="form-field-split my-4">
                        <div class="form-field-floating reactive sm">
                            <input v-model="txDateMin" type="date" class="form-input" name="date_min" id="date_min" placeholder="">
                            <label for="date_min">Date >=</label>
                        </div>
                        <hr class="hr sm" />
                        <div class="form-field-floating reactive sm">
                            <input v-model="txDateMax" type="date" class="form-input" name="date_max" id="date_max" placeholder="">
                            <label for="date_max">Date &lt;=</label>
                        </div>
                    </div>
                    <button class="btn lg shadow-200 color2" @click="applyTxFilters">
                        <span>Filtrer</span>
                    </button>
                </div>

                <div v-if="transactionsLoading" class="text-center py-5 text-grey-500">
                    Chargement des transactions...
                </div>

                <div v-else-if="transactions.length === 0" class="text-center py-5 text-grey-500">
                    Aucune transaction trouvée pour cette carte.
                </div>

                <div v-else class="s20">
                    <div v-for="tx in transactions" :key="tx.id" class="flex gap-6 mb-4">
                        <div class="square-xl px-4 py-4" style="background-color: rgba(0, 185, 172, 0.08);">
                            <img src="/icons/icon-trans-recharger.svg" width="100%" class="icon-neutral" />
                        </div>
                        <div class="s13 mt-2">
                            <h6 class="secondary-font font-semibold mb-1">{{ tx.libelle || tx.description || tx.reference || '-' }}</h6>
                            <p class="secondary-font text-sm text-grey-600 mb-2">
                                {{ formatDate(tx.created_at, 'DD MMM YYYY HH:mm') }}
                                <span v-if="tx.reference"> - {{ tx.reference }}</span>
                            </p>
                        </div>
                        <h5 class="s5 secondary-font text-right mt-2" :class="{ 'text-green': tx.type === 'credit' }">
                            {{ tx.type === 'credit' ? '+ ' : '- ' }}{{ formatAmount(tx.montant) }} F
                        </h5>
                    </div>

                    <div class="pagination sm justify-content-left mt-5">
                        <button class="page-link" :disabled="txPage === 1" @click="changePage(txPage - 1)">
                            <img src="/icons/icon-pagination-prev.svg" class="pagination-icon" alt="" />
                            Précedent
                        </button>
                        <span class="page-link active">{{ txPage }} / {{ totalPages }}</span>
                        <button class="page-link pl-4" :disabled="txPage >= totalPages" @click="changePage(txPage + 1)">
                            Suivant
                            <img src="/icons/icon-pagination-next.svg" class="pagination-icon ml-2" alt="" />
                        </button>
                    </div>
                </div>
            </div>
        </div>


        <div class="clear-both h-80"></div>

    </section>
</template>
